import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Play } from 'lucide-react';
import { useAuth } from '../../services/authentication/authContext';
import { canAccessTour, getTourForPath } from './tours';
import { useOnboardingStore } from './onboardingStore';

/** A contextual sidebar entry; unsupported pages do not offer an unrelated tour. */
export function GuidedToursButton() {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const { backendUser, accountSettings } = useAuth();
  const userId = useOnboardingStore(state => state.userId);
  const running = useOnboardingStore(state => state.running);
  const hydrating = useOnboardingStore(state => state.hydrating);
  const saveError = useOnboardingStore(state => state.saveError);
  const startTour = useOnboardingStore(state => state.startTour);
  const retryPersistence = useOnboardingStore(state => state.retryPersistence);
  const [retrying, setRetrying] = useState(false);
  const tour = getTourForPath(pathname, { includeSubpages: true, search });

  if (!tour || !canAccessTour(tour, backendUser, accountSettings)) return null;

  const retry = async () => {
    setRetrying(true);
    try {
      await retryPersistence();
    } finally {
      setRetrying(false);
    }
  };

  return (
    <li className="hidden lg:block">
      <button
        type="button"
        onClick={() => startTour(tour.id)}
        disabled={running || hydrating || userId !== backendUser?.id}
        className="flex items-center h-8 w-full justify-start gap-1.5 rounded-lg border border-borderGrey dark:border-borderGrey-dark bg-gray-50 dark:bg-gray-800 text-secondary dark:text-secondary-dark hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors px-2.5 py-0 text-xs font-semibold whitespace-nowrap focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-darkGreenColor dark:focus-visible:outline-darkGreenColor-dark disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Play className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
        {t('onboarding.guidedTours')}
        <span aria-hidden="true" className="ml-auto rounded bg-darkGreenColor dark:bg-darkGreenColor text-white dark:text-white px-1 py-0.5 text-[9px] font-bold leading-none tracking-wide">
          {t('onboarding.newBadge')}
        </span>
      </button>
      {saveError && (
        <div className="px-2 pt-1 text-xs text-muted dark:text-muted-dark" role="status">
          <p>{t('onboarding.progressSaveError')}</p>
          <button
            type="button"
            onClick={() => { void retry(); }}
            disabled={retrying}
            className="mt-1 text-primary dark:text-primary underline disabled:opacity-50"
          >
            {t('onboarding.retrySave')}
          </button>
        </div>
      )}
    </li>
  );
}
